"use client";

import * as React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Phone, Clock, MapPin, ShieldCheck, Radio, ArrowUpRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
import { MenuProvider, useMenu, type MenuItem } from "@/integrations/wordpress/WordPressMenuProvider";
function RecursiveFooterMenu({
  items,
  depth = 0
}: {
  items: MenuItem[];
  depth?: number;
}) {
  return <ul className={depth === 0 ? "space-y-2.5" : "mt-2 space-y-1.5 pl-3 border-l border-border"}>
      {items.map((item, i) => <li key={item.id || `item-${i}`} data-index={i} className="text-sm">
          <Link to={item.href || "#"} className="text-muted-foreground hover:text-foreground transition-colors inline-flex items-center gap-1.5">
            <span>{item.label}</span>
            {item.href?.startsWith("http") && <ArrowUpRight className="size-3 text-muted-foreground" aria-hidden="true" />}
          </Link>
          {item.children && item.children.length > 0 && <RecursiveFooterMenu items={item.children} depth={depth + 1} />}
        </li>)}
    </ul>;
}
function FooterNavigationConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="space-y-2.5 py-1">
        {[0, 1, 2, 3, 4].map(i => <div key={i} data-index={i} className="h-4 bg-muted animate-pulse rounded-md w-3/4" />)}
      </div>;
  }
  if (!menuItems || menuItems.length === 0) {
    const defaultServices = [{
      id: "s1",
      label: "Water & Flood Damage",
      href: "/WaterDamageFloodRestorationPage"
    }, {
      id: "s2",
      label: "Fire, Smoke & Odor",
      href: "/FireSmokeOdorRestorationPage"
    }, {
      id: "s3",
      label: "Mold Remediation",
      href: "/MoldRemediationPage"
    }, {
      id: "s4",
      label: "Sewage Cleanup",
      href: "/SewageCleanupPage"
    }, {
      id: "s5",
      label: "Biohazard Cleanup",
      href: "/BiohazardCleanupPage"
    }, {
      id: "s6",
      label: "Commercial & HOA Restoration",
      href: "/CommercialHOAPropertyRestorationPage"
    }];
    return <RecursiveFooterMenu items={defaultServices} />;
  }
  return <RecursiveFooterMenu items={menuItems} />;
}
const coverageAreas = [{
  region: "Maryland",
  places: "Montgomery, Prince George's, Frederick, Anne Arundel, Baltimore County & Howard"
}, {
  region: "Washington DC",
  places: "Northwest, Northeast, Capitol Hill, Downtown, Georgetown & Southeast Metro"
}, {
  region: "Northern Virginia",
  places: "Fairfax County, Arlington, Alexandria, Loudoun County & Prince William"
}];
const socialLinks = [{
  label: "LinkedIn",
  to: "https://linkedin.com"
}, {
  label: "Facebook",
  to: "https://facebook.com"
}, {
  label: "YouTube",
  to: "https://youtube.com"
}];
export default function Footer() {
  const shouldReduceMotion = useReducedMotion();
  const currentYear = new Date().getFullYear();
  return <section id="footer" data-nav="light" className="bg-background text-foreground border-t border-border pt-16 pb-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div initial={shouldReduceMotion ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 12
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.32,
        ease: [0.16, 1, 0.3, 1]
      }} className="mb-14 rounded-lg border border-border bg-muted/60 p-6 sm:p-8">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
            <div className="space-y-2">
              <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full border border-primary/20 bg-primary/10 text-primary text-xs font-mono font-semibold uppercase tracking-wider">
                <span className="size-2 rounded-full bg-primary animate-pulse" aria-hidden="true" />
                Live Dispatch Ready
              </div>
              <h2 className="text-xl sm:text-2xl font-bold font-default tracking-tight text-foreground">
                Need emergency property mitigation right now?
              </h2>
              <p className="text-sm sm:text-base text-muted-foreground font-default max-w-2xl">
                Direct technician deployment with no call center delays. We bill insurance adjusters directly and arrive ready to mitigate structural damage.
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-3 shrink-0">
              <Button asChild size="lg" className="font-bold text-sm bg-primary text-primary-foreground hover:bg-primary/90">
                <a href="tel:8554356631">
                  <Phone className="size-4 mr-1.5" aria-hidden="true" />
                  <span>855-435-6631</span>
                </a>
              </Button>
              <Button asChild variant="outline" size="lg" className="font-semibold text-sm">
                <Link to="https://clienthub.getjobber.com/hubs/476c5fe2-f16a-4108-ad33-1763e02c3dad/public/requests/4972587/new" newTab>
                  <span>Request online intake</span>
                </Link>
              </Button>
            </div>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-10 lg:gap-8 mb-14">
          <div className="lg:col-span-4 space-y-5">
            <div>
              <Link to="/">
                <WvcLogo className="h-12 w-auto" />
              </Link>
            </div>
            <p className="text-sm text-muted-foreground font-default leading-relaxed max-w-sm">
              TriServe provides 24/7 emergency water, fire, smoke, mold, and disaster reconstruction across Maryland, Washington DC, and Northern Virginia. Direct field technician response with zero call centers.
            </p>
            <div className="pt-1">
              <span className="text-xs uppercase font-mono tracking-wider text-muted-foreground font-semibold block mb-2">
                24/7 Emergency Line
              </span>
              <a href="tel:8554356631" className="inline-flex items-center gap-2 text-lg font-mono font-bold text-primary hover:underline">
                <Phone className="size-4 shrink-0" aria-hidden="true" />
                <span>855-435-6631</span>
              </a>
            </div>
            <div className="pt-2">
              <span className="text-xs uppercase font-mono tracking-wider text-muted-foreground font-semibold block mb-2">
                Operational Profiles
              </span>
              <div className="flex items-center gap-3">
                {socialLinks.map((social, i) => <Link key={social.label} data-index={i} to={social.to} newTab className="text-xs font-mono font-medium px-2.5 py-1 rounded border border-border bg-card text-muted-foreground hover:text-foreground hover:border-primary transition-colors">
                    {social.label}
                  </Link>)}
              </div>
            </div>
          </div>

          <div className="lg:col-span-3 space-y-4">
            <h3 className="text-sm font-bold font-default uppercase tracking-wider text-foreground">
              Services & Mitigation
            </h3>
            <MenuProvider menu_id="32">
              <FooterNavigationConsumer />
            </MenuProvider>
          </div>

          <div className="lg:col-span-3 space-y-4">
            <h3 className="text-sm font-bold font-default uppercase tracking-wider text-foreground">
              Mid-Atlantic Coverage
            </h3>
            <div className="space-y-3.5 text-sm">
              {coverageAreas.map((area, i) => <div key={area.region} data-index={i} className="space-y-1">
                  <div className="flex items-center gap-1.5 font-semibold text-foreground font-default">
                    <MapPin className="size-3.5 text-primary shrink-0" aria-hidden="true" />
                    <span>{area.region}</span>
                  </div>
                  <p className="text-xs text-muted-foreground font-default leading-relaxed pl-5">
                    {area.places}
                  </p>
                </div>)}
            </div>
          </div>

          <div className="lg:col-span-2 space-y-4">
            <h3 className="text-sm font-bold font-default uppercase tracking-wider text-foreground">
              Dispatch Command
            </h3>
            <div className="space-y-3 text-sm">
              <div className="space-y-1">
                <div className="flex items-center gap-1.5 text-xs font-mono font-semibold text-foreground">
                  <Clock className="size-3.5 text-primary shrink-0" aria-hidden="true" />
                  <span>24/7 Response</span>
                </div>
                <p className="text-xs text-muted-foreground leading-relaxed pl-5">
                  Mobile command units on 365-day standby.
                </p>
              </div>
              <div className="space-y-1">
                <div className="flex items-center gap-1.5 text-xs font-mono font-semibold text-foreground">
                  <Radio className="size-3.5 text-primary shrink-0" aria-hidden="true" />
                  <span>Response Window</span>
                </div>
                <p className="text-xs text-muted-foreground leading-relaxed pl-5">
                  Local deployment within 60 minutes across primary zones.
                </p>
              </div>
              <div className="space-y-1">
                <div className="flex items-center gap-1.5 text-xs font-mono font-semibold text-foreground">
                  <ShieldCheck className="size-3.5 text-primary shrink-0" aria-hidden="true" />
                  <span>Insurance</span>
                </div>
                <p className="text-xs text-muted-foreground leading-relaxed pl-5">
                  Direct adjuster claims handling and Xactimate billing.
                </p>
              </div>
            </div>
          </div>
        </div>

        <Separator className="my-8" />

        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 text-xs font-default text-muted-foreground">
          <p>
            {"©"} {currentYear} TriServe. All rights reserved. Direct local field crews serving Maryland, Washington DC, and Northern Virginia.
          </p>
          <div className="flex items-center gap-6">
            <Link to="/ContactRequestServicePage" className="hover:text-foreground transition-colors">
              Dispatch Verification
            </Link>
            <Link to="#" className="hover:text-foreground transition-colors">
              Privacy Policy
            </Link>
            <Link to="#" className="hover:text-foreground transition-colors">
              Terms of Service
            </Link>
          </div>
        </div>
      </div>
    </section>;
}